/**
 * ServizioRow — F-009.6. Riga editabile del catalogo: nome, prezzo (€),
 * stato attivo (toggle) + Salva (solo campi modificati) + Elimina (con conferma).
 * Presentazionale: callback e flag pending dall'esterno.
 */
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import type { ServizioView, UpdateServizioBody } from '../lib/servizi-client';

interface ServizioRowProps {
  servizio: ServizioView;
  onSave: (body: UpdateServizioBody) => void;
  onDelete: () => void;
  isSaving?: boolean;
  isDeleting?: boolean;
}

function centsToEuroInput(prezzoCents: string): string {
  const n = Number(prezzoCents);
  return Number.isNaN(n) ? '' : String(n / 100);
}

export function ServizioRow({ servizio, onSave, onDelete, isSaving, isDeleting }: ServizioRowProps) {
  const initialNome = servizio.nome;
  const initialEuro = centsToEuroInput(servizio.prezzoCents);
  const [nome, setNome] = useState(initialNome);
  const [euro, setEuro] = useState(initialEuro);
  const [attivo, setAttivo] = useState(servizio.attivo);

  const prezzo = Number(euro.replace(',', '.'));
  const prezzoValid = euro.trim() !== '' && Number.isFinite(prezzo) && prezzo >= 0;
  const nomeValid = nome.trim() !== '';
  const changed =
    nome.trim() !== initialNome || euro.trim() !== initialEuro || attivo !== servizio.attivo;
  const canSave = nomeValid && prezzoValid && changed && !isSaving;

  function save() {
    if (!canSave) return;
    const body: UpdateServizioBody = {};
    if (nome.trim() !== initialNome) body.nome = nome.trim();
    if (euro.trim() !== initialEuro) body.prezzoEuro = prezzo;
    if (attivo !== servizio.attivo) body.attivo = attivo;
    onSave(body);
  }

  return (
    <tr className="border-b last:border-0" data-servizio-id={servizio.id}>
      <td className="py-3 pr-4">
        <Input
          value={nome}
          onChange={(e) => setNome(e.target.value)}
          aria-label={`Nome servizio ${initialNome}`}
        />
      </td>
      <td className="py-3 pr-4">
        <div className="flex items-center gap-1">
          <span className="text-muted-foreground">€</span>
          <Input
            type="number"
            inputMode="decimal"
            min={0}
            step="1"
            value={euro}
            onChange={(e) => setEuro(e.target.value)}
            className="w-24"
            aria-label={`Prezzo ${initialNome} in euro`}
          />
        </div>
      </td>
      <td className="py-3 pr-4">
        <Button
          type="button"
          variant={attivo ? 'default' : 'outline'}
          size="sm"
          onClick={() => setAttivo((a) => !a)}
          aria-pressed={attivo}
        >
          {attivo ? 'Attivo' : 'Non attivo'}
        </Button>
      </td>
      <td className="py-3 text-right">
        <div className="flex justify-end gap-2">
          <Button size="sm" onClick={save} disabled={!canSave}>
            Salva
          </Button>
          <Button
            size="sm"
            variant="destructive"
            onClick={() => {
              if (window.confirm(`Eliminare il servizio "${initialNome}"?`)) onDelete();
            }}
            disabled={isDeleting}
          >
            Elimina
          </Button>
        </div>
      </td>
    </tr>
  );
}
